import Image from "next/image";
import { stringToHslColor } from "@/utils/utils";

type FallbackAvatarProps = {
  src?: string | null;
  name?: string;
  firstName?: string;
  lastName?: string;
  id?: string | number;
  alt?: string;
  className?: string;
  textClassName?: string;
};

const getInitials = ({
  name,
  firstName,
  lastName,
}: Pick<FallbackAvatarProps, "name" | "firstName" | "lastName">): string => {
  const joinInitials = (a?: string, b?: string) => {
    if (a && b) return `${a}.${b}`;
    return a ?? b ?? "";
  };

  const trimmedFirst = firstName?.trim();
  const trimmedLast = lastName?.trim();

  if (trimmedFirst || trimmedLast) {
    return joinInitials(trimmedFirst?.[0], trimmedLast?.[0]);
  }

  const trimmedName = name?.trim();
  if (!trimmedName) return "";

  const parts = trimmedName.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return joinInitials(parts[0][0], parts[1][0]);
  }

  return parts[0][0] ?? "";
};

const getColorSeed = ({
  name,
  firstName,
  lastName,
  id,
}: Pick<FallbackAvatarProps, "name" | "firstName" | "lastName" | "id">): string | null => {
  const trimmedName = name?.trim();
  if (trimmedName) return trimmedName;

  const combined = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  if (combined) return combined;

  if (id !== undefined && id !== null && String(id) !== "") {
    return String(id);
  }

  return null;
};

export function FallbackAvatar({
  src,
  name,
  firstName,
  lastName,
  id,
  alt = "",
  className = "",
  textClassName = "",
}: Readonly<FallbackAvatarProps>) {
  if (src) {
    return (
      <div className={`relative rounded-full overflow-hidden bg-gray-100 ${className}`}>
        <Image
          src={src}
          alt={alt || name || ""}
          fill
          sizes="128px"
          className="object-cover"
        />
      </div>
    );
  }

  const seed = getColorSeed({ name, firstName, lastName, id });
  const initials = getInitials({ name, firstName, lastName });
  const backgroundColor = seed ? stringToHslColor(seed) : undefined;

  return (
    <div
      className={`relative rounded-full overflow-hidden ${seed ? "" : "bg-gray-100"} ${className}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <span
        className={`absolute inset-0 flex items-center justify-center font-bold text-gray-700 ${textClassName}`}
      >
        {initials}
      </span>
    </div>
  );
}
